import { Controller } from "@hotwired/stimulus"

// Primer Brand's SubNav, without React.
//
// Narrow screens get a full-height overlay of the links behind a toggle;
// wide screens get hover/click dropdown submenus. The CSS media queries pick
// which is visible, so the controller only tracks open state. Everything is
// rendered closed and correctly labelled before this runs.
export default class extends Controller {
  static targets = ["nav", "inner", "toggle", "button", "overlay", "item", "itemButton", "itemChildren"]

  disconnect() {
    document.body.style.overflow = ""
  }

  // Narrow overlay --------------------------------------------------------

  toggle() {
    if (this.buttonTarget.getAttribute("aria-expanded") === "true") this.close()
    else this.open()
  }

  open() {
    this.setOpen(true)
    // The overlay scrolls inside what is left of the viewport below the nav.
    const top = this.navTarget.getBoundingClientRect().top
    this.navTarget.style.setProperty("--subnav-available-height", `${window.innerHeight - top}px`)
    document.body.style.overflow = "hidden"
  }

  close() {
    this.setOpen(false)
    this.navTarget.style.removeProperty("--subnav-available-height")
    document.body.style.overflow = ""
    for (const item of this.itemTargets) this.setItemExpanded(item, false)
  }

  closeOnOutsideClick(event) {
    if (!this.innerTarget.contains(event.target)) this.close()
  }

  setOpen(open) {
    for (const button of this.buttonTargets) button.setAttribute("aria-expanded", String(open))
    for (const toggle of this.toggleTargets) toggle.classList.toggle("SubNav__overlay-toggle--open", open)
    this.navTarget.classList.toggle("SubNav--open", open)
    if (this.hasOverlayTarget) this.overlayTarget.classList.toggle("SubNav__links-overlay--open", open)
  }

  // Dropdown submenus -----------------------------------------------------

  expand(event) {
    this.setItemExpanded(event.currentTarget, true)
  }

  collapse(event) {
    this.setItemExpanded(event.currentTarget, false)
  }

  toggleItem(event) {
    const item = event.currentTarget.closest(".SubNav__link--has-sub-menu")
    this.setItemExpanded(item, event.currentTarget.getAttribute("aria-expanded") !== "true")
  }

  collapseOnFocusOut(event) {
    const item = event.currentTarget
    if (!item.contains(event.relatedTarget)) this.setItemExpanded(item, false)
  }

  setItemExpanded(item, expanded) {
    item.classList.toggle("SubNav__link--expanded", expanded)
    item.querySelector(".SubNav__sub-menu-toggle")?.setAttribute("aria-expanded", String(expanded))
    // Only the wide-screen dropdown hides its children; the narrow overlay
    // shows them inline, as upstream does.
    const wide = window.matchMedia("(min-width: 63.25rem)").matches
    item.querySelector(".SubNav__sub-menu-children")?.toggleAttribute("inert", wide && !expanded)
  }
}
